import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import axios from 'axios';

import {HOST} from "../../utils/vars";
import TimedAlert from "../Alerts/TimedAlert";
import {Courier, Delivery, RentalEditFormState, RentalFormProps} from "../RentalList/RentalInterfaces";

interface Extra {
  id: number;
  name: string;
  price: number;
  extra_price: number;
}

interface Product {
  id: number;
  name: string;
  price_per_day: number;
  deposit: number;
}

interface DeliveryOption {
  id: number;
  name: string;
  price: number;
  is_fixed: boolean;
  extra_price: number;
}

const RentalForm: React.FC<RentalFormProps> = ({rental, onChange}) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [extras, setExtras] = useState<Extra[]>([]);
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState<string>('');

  const [form, setForm] = useState<RentalEditFormState>({
    productId: '',
    extras: [] as string[],
    deliveries: [{type: 'delivery', address: '', optionId: '', comment: ''}] as Delivery[],
    startDate: new Date().toLocaleDateString('en-CA'),
    endDate: null,
    status: 'создан'
  });

  useEffect(() => {
    axios.get(HOST + '/api/v1/products').then(res => setProducts(res.data));
    axios.get(HOST + '/api/v1/extras').then(res => setExtras(res.data));
    axios.get(HOST + '/api/v1/delivery-options').then(res => setDeliveryOptions(res.data));
    axios.get(HOST + '/api/v1/couriers').then(res => setCouriers(res.data));
  }, []);

  useEffect(() => {
    if (rental) {
      setForm({
        productId: rental?.product_id.toString() || '',
        extras: rental?.extras ? rental.extras.map(extra => extra.id.toString()) : [] as string[],
        deliveries: rental?.deliveries as Delivery[],
        startDate: new Date(rental?.start_date || "").toLocaleDateString('en-CA') || new Date().toLocaleDateString('en-CA'),
        endDate: new Date(rental?.end_date || "").toLocaleDateString('en-CA') || null,
        status: rental?.status || 'создан',
      })
    }
  }, []);

  useEffect(() => {
    if (onChange) {
      onChange(form);
    }
  }, [form, onChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const hasInvalidDelivery = form.deliveries.some(delivery => {
      return (
        !delivery.courier_name ||
        !delivery.optionId ||
        ((delivery.optionId !== '9') && !delivery.address) ||
        !delivery.customerName ||
        !delivery.phone
      );
    });

    if (hasInvalidDelivery) {
      setAlertText('Заповненi не всi поля!');
      setShowAlert(true);
      return;
    }

    try {
      await axios.post(HOST + '/api/v1/rentals', form);
      navigate('/');
    } catch (e) {
      console.log(e?.toString());
      setAlertText(`Не змiг створити оренду =( ${e?.toString()}`);
      setShowAlert(true);
      // alert('Something went wrong');
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-12">
          <div className="d-flex flex-column mb-3">
            <label className="form-label">Товар</label>
            <select
              className="form-select mb-2"
              value={form.productId}
              onChange={e => setForm({...form, productId: e.target.value})}
            >
              <option value="">Выберите товар</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.price_per_day}грн/день (залог {p.deposit}грн)
                </option>
              ))}
            </select>
          </div>

          {form.productId && (
            <div className="d-flex flex-column">
              <label className="form-label">Доп. товары</label>
              <div className="form-check">
                {extras.map((extra) => (
                  <div key={extra.id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value={extra.id}
                      checked={form.extras.includes(extra.id.toString())}
                      onChange={e => {
                        const id = e.target.value;
                        const newExtras = e.target.checked
                          ? [...form.extras, id]
                          : form.extras.filter((x) => x !== id);
                        setForm({...form, extras: newExtras});
                      }}
                    />
                    <label className="form-check-label ms-2">
                      {extra.name} — {Number(extra.price)}грн {Number(extra.extra_price) !== 0 ?
                      ` залог + ${Number(extra.extra_price)}грн/доба` : ''}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {form.productId && (
          <div>
            <div className="col-md-6">
              <label className="form-label">Дата начала</label>
              <input
                type="date"
                className="form-control"
                value={form.startDate || new Date().toLocaleDateString('en-CA')}
                onChange={e => setForm({...form, startDate: e.target.value})}
              />
            </div>

            <br/>

            {rental?.end_date && (
              <div className="col-md-6">
                <label className="form-label">Дата закiнчення</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.endDate || new Date().toLocaleDateString('en-CA')}
                  onChange={e => setForm({...form, endDate: e.target.value})}
                />
              </div>
            )}

            <br/>

            <div className="col-12">
              <label className="form-label">Курьер</label>
              {form.deliveries.map((delivery, index) => (
                <div key={index}>
                  <div className="d-flex flex-column border rounded p-3 mb-3">

                    <label className="form-label">Курьер</label>
                    <select
                      className="form-select mb-2"
                      value={delivery.courier_name || ''}
                      onChange={e => {
                        const newDeliveries = [...form.deliveries];
                        newDeliveries[index].courier_name = e.target.value;
                        setForm({...form, deliveries: newDeliveries});
                      }}
                    >
                      <option value="">Выберите курьера</option>
                      {couriers.map(courier => (
                        <option key={courier.username} value={courier.username}>
                          {courier.username}
                        </option>
                      ))}
                    </select>


                    <label className="form-label">Тип доставки</label>
                    <select
                      className="form-select mb-2"
                      value={delivery.optionId}
                      onChange={e => {
                        const newDeliveries = [...form.deliveries];
                        newDeliveries[index].optionId = e.target.value;
                        setForm({...form, deliveries: newDeliveries});
                      }}
                    >
                      <option value="">Тип доставки</option>
                      {deliveryOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name} ({opt.price}грн {opt.is_fixed ? '' : '+ ' + opt.extra_price + 'грн за км'})
                        </option>
                      ))}
                    </select>

                    <label className="form-label">Адрес доставки</label>
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder="Адрес доставки"
                      value={delivery.address}
                      disabled={delivery.optionId === '9' || delivery.optionId === ''}
                      onChange={e => {
                        const newDeliveries = [...form.deliveries];
                        newDeliveries[index].address = e.target.value;
                        setForm({...form, deliveries: newDeliveries});
                      }}
                    />

                    <label className="form-label">Имя</label>
                    <input
                      type="text"
                      className="form-control mb-2"
                      placeholder="Имя"
                      value={(delivery.customerName || delivery.customer_name) || ''}
                      onChange={e => {
                        const newDeliveries = [...form.deliveries];
                        newDeliveries[index].customerName = e.target.value;
                        setForm({...form, deliveries: newDeliveries});
                      }}
                    />

                    <label className="form-label">Телефон</label>
                    <input
                      type="tel"
                      className="form-control mb-2"
                      placeholder="Телефон"
                      value={delivery.phone || ''}
                      onChange={e => {
                        const newDeliveries = [...form.deliveries];
                        newDeliveries[index].phone = e.target.value;
                        setForm({...form, deliveries: newDeliveries});
                      }}
                    />

                    <label className="form-label">Коментарий</label>
                    <input
                      type="comment"
                      className="form-control mb-2"
                      placeholder="Коментарий"
                      value={delivery.comment || ''}
                      onChange={e => {
                        const newDeliveries = [...form.deliveries];
                        newDeliveries[index].comment = e.target.value;
                        setForm({...form, deliveries: newDeliveries});
                      }}
                    />
                  </div>
                  <br/>
                </div>
              ))}
            </div>
          </div>
        )}

        {!rental && (
          <div className="col-12">
            <button className="btn btn-primary" type="submit">
              Створити аренду
            </button>
            <Link to="/" className="btn btn-secondary" style={{marginLeft: 10}}>Назад</Link>
          </div>
        )}

      </form>

      <TimedAlert
        text='Заповненi не всi поля!'
        type="warning"          // 'danger', 'info', 'warning', 'primary'
        show={showAlert}
        onClose={() => setShowAlert(false)}
        duration={5000}
      />
    </div>
  );
};

export default RentalForm;
